import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Button, Input, AutoComplete, Drawer } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import Marquee from "../Marquee";
import { ComponentContainer, LayoutGrid } from "../LayoutContainers";
import GraphCarousel from "../GraphCarousel";
import CardWithTooltip from "../CardWithTooltip";
import { SkeletonHeroPrices } from "../skeletons/home";
import { fetchPaneles, fetchCotizaciones, fetchIndices, fetchSearchItems } from "../../utils/fetchers";
import { formatPriceToAR, createAutoCompleteOptions } from "../../utils/utils";
import useLocale from "../../hooks/useLocale";
import { __ } from "../../locales/translation";

export default function HeroHome({ theme, componentId, tag, title }) {
    const locale = useLocale();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const results = useQueries({
        queries: [
            { queryKey: ["indices"], queryFn: fetchIndices },
            { queryKey: ["paneles"], queryFn: fetchPaneles },
            { queryKey: ["cotizaciones"], queryFn: fetchCotizaciones },
        ],
    });

    const { isLoading: isLoadingSearchItems, data: searchItems } = useQuery({
        queryKey: ["searchItems"],
        queryFn: fetchSearchItems,
    });

    const autoCompleteOptions = createAutoCompleteOptions(searchItems);

    return (
        <ComponentContainer className="pb-4" image={`${window._env_.STATIC_HOST}/static/website/images/bg-cotizaciones.jpg`}>
            <LayoutGrid spacingTop="0" spacingBottom="0" cols={1}>
                <section
                    className={`hero --cotizaciones component ${theme ? `--theme-${theme}` : ""}`}
                    id={componentId || ""}
                >
                    <div className="flex-content">
                        {/* Title and Searchbar */}
                        <div className="hero-header mb-1">
                            <div className="text-content">
                                <div className="tag">{tag}</div>

                                {title && <h1 className="title-1">{title}</h1>}
                            </div>

                            <AutoComplete
                                disabled={isLoadingSearchItems}
                                options={autoCompleteOptions}
                                className="search-bar"
                                onSelect={(_, record) => {
                                    if (record.id) {
                                        navigate(`/fondos/${record.id}`);
                                    } else if (record.code) {
                                        navigate(`/${record.type}/${encodeURIComponent(record.code)}`);
                                    }
                                }}
                                filterOption={(inputValue, option) =>
                                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                }
                            >
                                <Input.Search
                                    size="large"
                                    placeholder={__("Buscá por cotizaciones, títulos o empresas")}
                                    className="--theme-dark"
                                    loading={isLoadingSearchItems}
                                />
                            </AutoComplete>

                            <SearchBtn
                                open={drawerOpen}
                                setOpen={setDrawerOpen}
                                autoCompleteOptions={autoCompleteOptions}
                            />
                        </div>
                    </div>
                </section>
            </LayoutGrid>

            {/* Marquee */}
            <Marquee isLoading={results[0].isLoading} content={results[0].data?.indices || null} bgColor="#111111" />

            <LayoutGrid spacingTop="0" spacingBottom="4" cols={1}>
                <div className="graph-cards-container">
                    <div className="graph-wrapper pt-2">
                        <GraphCarousel
                            isLoading={results[1].isLoading}
                            content={results[1].data?.paneles || null}
                            theme={"dark"}
                            componentId="graphCarousel"
                            bgColor="#111111"
                            spacingTop="20"
                            spacingBottom="20"
                        />
                    </div>

                    <div className="cards-wrapper">
                        {results[1].isLoading ? (
                            <SkeletonHeroPrices />
                        ) : (
                            results[2].data?.cotizaciones_moneda.map((item) => (
                                <CardWithTooltip
                                    key={item.tipo[locale] + item.variacion}
                                    variacion={item.variacion}
                                    label={item.tipo[locale]}
                                    buyPrice={formatPriceToAR(item.precioCompra)}
                                    sellPrice={formatPriceToAR(item.precioVenta)}
                                    tooltipText={item.info[locale]}
                                />
                            ))
                        )}
                    </div>
                </div>
            </LayoutGrid>
        </ComponentContainer>
    );
}

function SearchBtn({ classNames, open, setOpen, autoCompleteOptions }) {
    const navigate = useNavigate();

    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
        <>
            <Drawer
                title=" "
                placement={"bottom"}
                closable={false}
                onClose={onClose}
                open={open}
                extra={<CloseIcon onClick={() => setOpen(false)} />}
                height={"100%"}
            >
                <AutoComplete
                    options={autoCompleteOptions}
                    className="search-bar"
                    filterOption={(inputValue, option) =>
                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    onSelect={(_, record) => {
                        if (record.code) {
                            navigate(`/${record.type}/${encodeURIComponent(record.code)}`);
                        }
                    }}
                >
                    <Input.Search
                        size="large"
                        placeholder={__("Buscá por cotizaciones, títulos o empresas")}
                        className="--theme-dark"
                    />
                </AutoComplete>
            </Drawer>

            <Button
                onClick={showDrawer}
                size="large"
                className={`cot-search-btn ${classNames}`}
                icon={<SearchOutlined />}
            />
        </>
    );
}

function CloseIcon({ onClick }) {
    return (
        <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={onClick}
        >
            <path
                d="M8.5026 14.6673C4.82541 14.6673 1.83594 11.6778 1.83594 8.00065C1.83594 4.32346 4.82541 1.33398 8.5026 1.33398C12.1798 1.33398 15.1693 4.32346 15.1693 8.00065C15.1693 11.6778 12.1798 14.6673 8.5026 14.6673Z"
                stroke="white"
            />
            <path
                d="M6.12683 11C5.96751 11 5.80818 10.9413 5.68239 10.8156C5.4392 10.5725 5.4392 10.1701 5.68239 9.927L10.4287 5.18233C10.6719 4.93922 11.0744 4.93922 11.3176 5.18233C11.5608 5.42543 11.5608 5.8278 11.3176 6.0709L6.57128 10.8156C6.45388 10.9413 6.28616 11 6.12683 11Z"
                fill="white"
            />
            <path
                d="M10.8732 11C10.7138 11 10.5545 10.9413 10.4287 10.8156L5.68239 6.0709C5.4392 5.8278 5.4392 5.42543 5.68239 5.18233C5.92558 4.93922 6.32809 4.93922 6.57128 5.18233L11.3176 9.927C11.5608 10.1701 11.5608 10.5725 11.3176 10.8156C11.1918 10.9413 11.0325 11 10.8732 11Z"
                fill="white"
            />
        </svg>
    );
}
